import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTheme } from '../../../hooks/useTheme';
import ParticleBackground from '../../../components/base/ParticleBackground';

export default function RWAApplications() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation();
  const { ref: bottomRef, isVisible: bottomVisible } = useScrollAnimation();

  const applications = [
    {
      titleKey: 'rwa.realestate',
      descKey: 'rwa.realestate.desc',
      icon: 'ri-building-line',
      tag: 'Real Estate',
      image:
        'https://readdy.ai/api/search-image?query=modern%20luxury%20real%20estate%20building%20with%20clean%20architectural%20lines%20and%20glass%20facades%20in%20minimalist%20style%2C%20professional%20photography%20with%20soft%20natural%20lighting%2C%20simple%20clean%20background%20with%20subtle%20warm%20orange%20tones%2C%20high-end%20property%20investment%20concept%2C%20ultra-detailed%208k%20quality&width=400&height=480&seq=rwa-realestate-003&orientation=portrait',
    },
    {
      titleKey: 'rwa.energy',
      descKey: 'rwa.energy.desc',
      icon: 'ri-flashlight-line',
      tag: 'Energy',
      image:
        'https://readdy.ai/api/search-image?query=clean%20energy%20infrastructure%20with%20solar%20panels%20and%20wind%20turbines%20in%20minimalist%20composition%2C%20soft%20orange%20and%20warm%20tones%2C%20simple%20clean%20background%2C%20sustainable%20resource%20management%20concept%2C%20professional%20photography%20with%20natural%20lighting%20and%20modern%20environmental%20technology%2C%20ultra-detailed%208k%20quality&width=400&height=480&seq=rwa-energy-003&orientation=portrait',
    },
    {
      titleKey: 'rwa.commodity',
      descKey: 'rwa.commodity.desc',
      icon: 'ri-truck-line',
      tag: 'Commodity',
      image:
        'https://readdy.ai/api/search-image?query=global%20supply%20chain%20and%20logistics%20network%20visualization%20with%20shipping%20containers%20and%20cargo%20in%20minimalist%20style%2C%20neutral%20gray%20and%20warm%20orange%20tones%20background%2C%20modern%20trade%20and%20commodity%20concept%2C%20clean%20professional%20photography%20with%20soft%20lighting%2C%20organized%20warehouse%20setting%2C%20ultra-detailed%208k%20quality&width=400&height=480&seq=rwa-commodity-003&orientation=portrait',
    },
    {
      titleKey: 'rwa.structured',
      descKey: 'rwa.structured.desc',
      icon: 'ri-line-chart-line',
      tag: 'Yield',
      image:
        'https://readdy.ai/api/search-image?query=financial%20data%20visualization%20with%20elegant%20charts%20and%20graphs%20showing%20structured%20yields%20and%20investment%20returns%2C%20soft%20orange%20and%20warm%20tones%20background%2C%20minimalist%20business%20analytics%20concept%2C%20professional%20photography%20with%20soft%20lighting%2C%20clean%20modern%20design%20aesthetic%2C%20ultra-detailed%208k%20quality&width=400&height=480&seq=rwa-structured-003&orientation=portrait',
    },
  ];

  return (
    <section
      id="rwa"
      className={`py-16 md:py-24 lg:py-28 relative overflow-hidden transition-colors duration-500 ${
        isDark
          ? 'bg-gradient-to-b from-[#161b22] to-[#0d1117]'
          : 'bg-gradient-to-b from-[#f8fafb] to-white'
      }`}
    >
      {/* Background Decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className={`absolute top-20 left-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-to-br ${
            isDark ? 'from-[#E84A1F]/10' : 'from-[#E84A1F]/5'
          } to-transparent rounded-full blur-3xl`}
        ></div>
        <div
          className={`absolute bottom-0 right-0 w-[250px] md:w-[500px] h-[250px] md:h-[500px] bg-gradient-to-tl ${
            isDark ? 'from-[#F06030]/10' : 'from-[#F06030]/5'
          } to-transparent rounded-full blur-3xl`}
        ></div>

        {/* 中心渐变 */}
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[900px] h-[500px] md:h-[900px] bg-gradient-radial ${
            isDark ? 'from-[#E84A1F]/5' : 'from-[#E84A1F]/3'
          } via-transparent to-transparent blur-[60px]`}
        ></div>

        {/* Grid Pattern */}
        <div
          className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.02]'}`}
          style={{
            backgroundImage: `linear-gradient(${isDark ? '#fff' : '#0d1117'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? '#fff' : '#0d1117'} 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        ></div>

        {/* 六边形网格 */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l25.98 15v30L30 60 4.02 45V15z' fill='none' stroke='%23E84A1F' stroke-width='0.5'/%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px',
          }}
        ></div>

        {/* 粒子背景效果 */}
        <ParticleBackground 
          particleCount={50} 
          speed={0.4} 
          connectDistance={90}
        />

        {/* 装饰圆环 */}
        <div
          className={`absolute top-[5%] right-[10%] w-[100px] md:w-[200px] h-[100px] md:h-[200px] border ${
            isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'
          } rounded-full hidden md:block`}
        ></div>
        <div
          className={`absolute bottom-[10%] left-[8%] w-[150px] md:w-[250px] h-[150px] md:h-[250px] border border-dashed ${
            isDark ? 'border-[#F06030]/10' : 'border-[#F06030]/5'
          } rounded-full hidden md:block`}
        ></div>

        {/* 渐变波浪 */}
        <svg
          className="absolute top-0 left-0 w-full h-[100px] md:h-[150px] opacity-[0.03]"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#E84A1F"
            d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
        </svg>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header with Section Number */}
        <div
          ref={headerRef}
          className={`relative mb-10 md:mb-16 transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Large Background Number */}
          <div
            className={`absolute -top-8 left-0 text-[100px] md:text-[180px] font-bold ${
              isDark ? 'text-white/[0.02]' : 'text-[#0d1117]/[0.02]'
            } leading-none select-none pointer-events-none hidden md:block`}
          >
            05
          </div>

          <div className="flex flex-col md:flex-row items-start justify-between relative gap-4 md:gap-0">
            <div className="flex items-center gap-4 md:gap-6">
              {/* 左侧编号栏 */}
              <div className="flex md:flex-col items-center gap-3 pt-0 md:pt-2">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
                  <span className="text-white font-bold text-base md:text-lg">05</span>
                </div>
                <div
                  className={`hidden md:block w-px bg-gradient-to-b from-[#E84A1F] to-transparent transition-all duration-1000 delay-300 ${
                    headerVisible ? 'h-16' : 'h-0'
                  }`}
                ></div>
              </div>
              <div>
                <div className="inline-flex items-center gap-3 mb-2 md:mb-3">
                  <div className="w-2 h-2 bg-[#E84A1F] rounded-full animate-pulse"></div>
                  <span className="text-xs text-[#E84A1F] tracking-[0.2em] md:tracking-[0.3em] uppercase">
                    RWA Applications
                  </span>
                </div>
                <h2
                  className={`text-3xl md:text-4xl lg:text-5xl font-bold transition-colors duration-500 ${
                    isDark ? 'text-white' : 'text-[#0d1117]'
                  }`}
                >
                  {t('rwa.title')}
                </h2>
              </div>
            </div>
            <div
              className={`max-w-full md:max-w-[320px] text-sm leading-relaxed pt-0 md:pt-6 border-l-0 md:border-l-2 border-[#E84A1F]/20 pl-0 md:pl-6 transition-all duration-1000 delay-500 ${
                isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
              } ${headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-10'}`}
            >
              {t('rwa.subtitle')}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div
          ref={cardsRef}
          className={`backdrop-blur-sm rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 border shadow-xl transition-all duration-1000 ${
            isDark
              ? 'bg-[#161b22]/60 border-white/5 shadow-black/20'
              : 'bg-white/60 border-[#0d1117]/5 shadow-[#0d1117]/5'
          } ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
        >
          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-6 md:mb-8">
            {applications.map((app, index) => (
              <div
                key={index}
                className={`group cursor-pointer rounded-xl md:rounded-2xl overflow-hidden border hover:border-[#E84A1F]/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-[#E84A1F]/10 relative ${
                  isDark ? 'bg-[#0d1117] border-white/5' : 'bg-white border-[#0d1117]/5'
                } ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/0 to-[#F06030]/0 group-hover:from-[#E84A1F]/5 group-hover:to-[#F06030]/5 transition-all duration-500 pointer-events-none"></div>

                {/* Image Area */}
                <div
                  className={`relative overflow-hidden h-[160px] sm:h-[180px] md:h-[200px] ${
                    isDark
                      ? 'bg-gradient-to-br from-[#1a1a24] to-[#0d1117]'
                      : 'bg-gradient-to-br from-[#f0f4f5] to-[#e8ecee]'
                  }`}
                >
                  <img
                    src={app.image}
                    alt={t(app.titleKey)}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Tag */}
                  <div
                    className={`absolute top-3 left-3 px-2.5 md:px-3 py-1 backdrop-blur-sm rounded-full text-xs font-medium shadow-sm ${
                      isDark ? 'bg-[#0d1117]/90 text-gray-300' : 'bg-white/90 text-[#0d1117]/70'
                    }`}
                  >
                    {app.tag}
                  </div>

                  {/* Icon Overlay */}
                  <div className="absolute bottom-3 right-3 w-9 md:w-10 h-9 md:h-10 bg-gradient-to-br from-[#E84A1F] to-[#F06030] rounded-lg md:rounded-xl flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                    <i className={`${app.icon} text-base md:text-lg text-white`}></i>
                  </div>
                </div>

                {/* Info Area */}
                <div className="p-4 md:p-5 relative">
                  {/* Index Number */}
                  <div
                    className={`absolute top-3 md:top-4 right-3 md:right-4 text-2xl md:text-3xl font-bold group-hover:text-[#E84A1F]/10 transition-colors duration-500 ${
                      isDark ? 'text-white/5' : 'text-[#0d1117]/5'
                    }`}
                  >
                    0{index + 1}
                  </div>
                  <h3
                    className={`text-sm md:text-base font-semibold mb-1.5 md:mb-2 group-hover:text-[#E84A1F] transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-[#0d1117]'
                    }`}
                  >
                    {t(app.titleKey)}
                  </h3>
                  <p
                    className={`text-xs md:text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}
                  >
                    {t(app.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Divider with Icon */}
          <div className="flex items-center gap-4 mb-6 md:mb-8">
            <div
              className={`flex-1 h-px bg-gradient-to-r from-transparent ${
                isDark ? 'via-white/10' : 'via-[#0d1117]/10'
              } to-transparent`}
            ></div>
            <div className="w-9 md:w-10 h-9 md:h-10 rounded-full bg-gradient-to-br from-[#E84A1F]/10 to-[#F06030]/10 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-[#E84A1F]"></i>
            </div>
            <div
              className={`flex-1 h-px bg-gradient-to-r from-transparent ${
                isDark ? 'via-white/10' : 'via-[#0d1117]/10'
              } to-transparent`}
            ></div>
          </div>

          {/* Bottom Info Section */}
          <div ref={bottomRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <div
              className={`group rounded-xl md:rounded-2xl p-5 md:p-6 border hover:border-[#E84A1F]/20 transition-all duration-500 relative overflow-hidden ${
                isDark
                  ? 'bg-gradient-to-br from-[#0d1117] to-[#161b22] border-white/5'
                  : 'bg-gradient-to-br from-[#fafbfc] to-white border-[#0d1117]/5'
              } ${bottomVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:-translate-x-10'}`}
            >
              {/* Background Decoration */}
              <div
                className={`absolute -right-10 -bottom-10 w-32 md:w-40 h-32 md:h-40 bg-gradient-to-br ${
                  isDark ? 'from-[#E84A1F]/10' : 'from-[#E84A1F]/5'
                } to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700`}
              ></div>

              <div className="relative flex gap-4 md:gap-5">
                <div className="flex-shrink-0">
                  <div className="w-12 md:w-14 h-12 md:h-14 bg-gradient-to-br from-[#E84A1F] to-[#F06030] rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20 group-hover:scale-110 transition-transform duration-500">
                    <i className="ri-links-line text-xl md:text-2xl text-white"></i>
                  </div>
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-base md:text-lg font-semibold mb-1.5 md:mb-2 group-hover:text-[#E84A1F] transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-[#0d1117]'
                    }`}
                  >
                    {t('rwa.onchain.title')}
                  </h3>
                  <p
                    className={`text-xs md:text-sm leading-relaxed mb-1.5 md:mb-2 ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}
                  >
                    {t('rwa.onchain.desc')}
                  </p>
                  <p className={`text-xs leading-relaxed hidden sm:block ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>
                    {t('rwa.onchain.note')}
                  </p>
                </div>
              </div>

              {/* Feature Tags */}
              <div
                className={`flex flex-wrap gap-2 mt-3 md:mt-4 pt-3 md:pt-4 border-t ${
                  isDark ? 'border-white/5' : 'border-[#0d1117]/5'
                }`}
              >
                <span className="px-2.5 md:px-3 py-1 bg-[#E84A1F]/5 rounded-full text-xs text-[#E84A1F]">
                  {t('rwa.onchain.tag1')}
                </span>
                <span
                  className={`px-2.5 md:px-3 py-1 rounded-full text-xs ${
                    isDark ? 'bg-white/5 text-gray-400' : 'bg-[#0d1117]/5 text-[#0d1117]/50'
                  }`}
                >
                  {t('rwa.onchain.tag2')}
                </span>
                <span
                  className={`px-2.5 md:px-3 py-1 rounded-full text-xs hidden sm:inline-block ${
                    isDark ? 'bg-white/5 text-gray-400' : 'bg-[#0d1117]/5 text-[#0d1117]/50'
                  }`}
                >
                  {t('rwa.onchain.tag3')}
                </span>
              </div>
            </div>

            <div
              className={`group rounded-xl md:rounded-2xl p-5 md:p-6 border hover:border-[#E84A1F]/20 transition-all duration-500 relative overflow-hidden ${
                isDark
                  ? 'bg-gradient-to-br from-[#0d1117] to-[#161b22] border-white/5'
                  : 'bg-gradient-to-br from-[#fafbfc] to-white border-[#0d1117]/5'
              } ${bottomVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-10'}`}
              style={{ transitionDelay: '150ms' }}
            >
              {/* Background Decoration */}
              <div
                className={`absolute -right-10 -bottom-10 w-32 md:w-40 h-32 md:h-40 bg-gradient-to-br ${
                  isDark ? 'from-[#F06030]/10' : 'from-[#F06030]/5'
                } to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700`}
              ></div>

              <div className="relative flex gap-4 md:gap-5">
                <div className="flex-shrink-0">
                  <div className="w-12 md:w-14 h-12 md:h-14 bg-gradient-to-br from-[#F06030] to-[#C83A15] rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-[#F06030]/20 group-hover:scale-110 transition-transform duration-500">
                    <i className="ri-shield-check-line text-xl md:text-2xl text-white"></i>
                  </div>
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-base md:text-lg font-semibold mb-1.5 md:mb-2 group-hover:text-[#E84A1F] transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-[#0d1117]'
                    }`}
                  >
                    {t('rwa.compliance.title')}
                  </h3>
                  <p
                    className={`text-xs md:text-sm leading-relaxed mb-1.5 md:mb-2 ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}
                  >
                    {t('rwa.compliance.desc')}
                  </p>
                  <p className={`text-xs leading-relaxed hidden sm:block ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>
                    {t('rwa.compliance.note')}
                  </p>
                </div>
              </div>

              {/* Feature Tags */}
              <div
                className={`flex flex-wrap gap-2 mt-3 md:mt-4 pt-3 md:pt-4 border-t ${
                  isDark ? 'border-white/5' : 'border-[#0d1117]/5'
                }`}
              >
                <span className="px-2.5 md:px-3 py-1 bg-[#E84A1F]/5 rounded-full text-xs text-[#E84A1F]">
                  {t('rwa.compliance.tag1')}
                </span>
                <span
                  className={`px-2.5 md:px-3 py-1 rounded-full text-xs ${
                    isDark ? 'bg-white/5 text-gray-400' : 'bg-[#0d1117]/5 text-[#0d1117]/50'
                  }`}
                >
                  {t('rwa.compliance.tag2')}
                </span>
                <span
                  className={`px-2.5 md:px-3 py-1 rounded-full text-xs hidden sm:inline-block ${
                    isDark ? 'bg-white/5 text-gray-400' : 'bg-[#0d1117]/5 text-[#0d1117]/50'
                  }`}
                >
                  {t('rwa.compliance.tag3')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div className="mt-6 md:mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-8 md:gap-12">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-2 h-2 rounded-full bg-[#E84A1F]"></div>
            <span className={`text-xs md:text-sm ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>4 Asset Classes</span>
          </div>
          <div className={`w-px h-4 hidden sm:block ${isDark ? 'bg-white/10' : 'bg-[#0d1117]/10'}`}></div>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-2 h-2 rounded-full bg-[#F06030]"></div>
            <span className={`text-xs md:text-sm ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>Full Lifecycle Support</span>
          </div>
          <div className={`w-px h-4 hidden sm:block ${isDark ? 'bg-white/10' : 'bg-[#0d1117]/10'}`}></div>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-2 h-2 rounded-full bg-[#C83A15]"></div>
            <span className={`text-xs md:text-sm ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>Global Compliance</span>
          </div>
        </div>
      </div>
    </section>
  );
}
